import { useState } from "react";
import styles from "./DataTable.module.css";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { Spinner } from "./Spinner";

const TONE_CLASS = { danger: styles.rowDanger, warning: styles.rowWarning };

/**
 * columns: [{ key, header, render?(row) }]
 * rowKey: the property holding a row's identity, or a FUNCTION deriving one.
 *   A grouped or aggregated table has no id column -- P&L rows are identified
 *   by year AND season -- and passing `(r) => ...` used to be accepted
 *   silently and then read as `keyOf(row)`, which is undefined for every
 *   row. React then keyed the whole table `undefined` (reordering reuses the
 *   wrong row's DOM) and selection compared undefined to undefined, so
 *   ticking one box would have ticked all of them.
 * selection: { selectedIds: Set, onToggle(id), onToggleAll(), idKey } -- omit to disable checkboxes
 * pageSize: rows per page (default 50). A table holding more rows than this
 *   pages itself -- "Page 1 of 3", Previous / Next and a jump-to-page box
 *   (#566) -- so no history is an ever-growing list. Pass `paginate={false}`
 *   for a table whose caller already pages from the server (it never holds
 *   more than a page, so this would never show anyway).
 * rowTone: optional (row) => "danger" | "warning" | null, for marking rows
 *   that need attention (#206). Styling only -- the caller is still expected
 *   to say why in a cell, since a tint alone is not readable in sunlight or
 *   to a colour-blind user.
 */
export function DataTable({ columns, rows, rowKey = "id", loading, emptyTitle = "Nothing here yet", emptyDescription, emptyAction, selection, onRowClick, rowTone, pageSize = 50, paginate = true }) {
  const [requested, setPage] = useState(1);
  const [jump, setJump] = useState("");
  if (loading) return <Spinner />;
  if (!rows || rows.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;

  const pages = paginate ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  // Clamped on read rather than reset in an effect: a filter that shrinks
  // the list must not leave the reader on a page that no longer exists.
  const page = Math.min(Math.max(requested, 1), pages);
  const shown = pages > 1 ? rows.slice((page - 1) * pageSize, page * pageSize) : rows;
  const goTo = (n) => setPage(Math.min(Math.max(Number(n) || 1, 1), pages));

  const keyOf = typeof rowKey === "function" ? rowKey : (r) => r[rowKey];

  const allSelected = selection && rows.length > 0 && rows.every((r) => selection.selectedIds.has(keyOf(r)));

  return (
    <div className={styles.scrollWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {selection && (
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={allSelected}
                  onChange={() => selection.onToggleAll(rows.map((r) => keyOf(r)))}
                  className={styles.checkbox}
                />
              </th>
            )}
            {columns.map((col) => (
              <th key={col.key}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <tr
              key={keyOf(row)}
              className={[onRowClick ? styles.clickable : null, TONE_CLASS[rowTone?.(row)]].filter(Boolean).join(" ") || undefined}
              onClick={() => onRowClick?.(row)}
            >
              {selection && (
                <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select row ${keyOf(row)}`}
                    checked={selection.selectedIds.has(keyOf(row))}
                    onChange={() => selection.onToggle(keyOf(row))}
                    className={styles.checkbox}
                  />
                </td>
              )}
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pages > 1 && (
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "var(--space-3)", flexWrap: "wrap",
            marginTop: "var(--space-3)", paddingTop: "var(--space-3)",
            borderTop: "1px solid var(--color-border)",
          }}
        >
          <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            Page {page} of {pages} ({rows.length} rows)
          </span>
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
            <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => goTo(page - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => goTo(page + 1)}>
              Next
            </Button>
            <form
              style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}
              onSubmit={(e) => { e.preventDefault(); goTo(jump); setJump(""); }}
            >
              <input
                type="number" min="1" max={pages} value={jump} placeholder="Page"
                aria-label="Go to page"
                onChange={(e) => setJump(e.target.value)}
                style={{ width: "5rem" }}
              />
              <Button size="sm" variant="secondary" type="submit">Go</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
